import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { IoFunnel } from "react-icons/io5";
import { Row, Col } from "react-bootstrap";
import moment from "moment";
import isEmpty from "lodash/isEmpty";

import * as projectActions from "../../actions/projectActions";
import * as codeActions from "../../actions/codeActions";
import * as userActions from "../../actions/userActions";
import * as serviceActions from "../../actions/serviceActions";
import * as callActions from "../../actions/callActions";
import * as customerActions from "../../actions/customerActions";
import * as modalActions from "../../actions/modalActions";

import CallService from "../../services/callService";
import CustomerService from "../../services/customerService";

import HeaderWrapper from "../common/HeaderWrapper";
import CallList from "../call/CallList";
import CallForm from "../call/CallForm";
import FiltersComponent from "../common/FiltersComponent";
import Modal from "../common/Modal";
import ConfirmModal from "../common/ConfirmModal";

const INITIAL_STATE = {
  customer: {},
  call: {},
  adding: false,
  open: false,
  filters: {},
  focusedInput: null,
  callsData: [],
  pagesData: null,
  deleteRecurring: false,
  recurring: false,
  allCalls: [],
  contract: {}
};

const CustomerCallsPage = ({
  isMobile,
  actions,
  users,
  services,
  contracts,
  auth,
  codes,
  savingCall,
  callToDelete,
  project,
  projects,
  call
}) => {
  const [s, setState] = useState({ ...INITIAL_STATE });
  const [loading, setLoading] = useState(false);

  const { id } = useParams();

  useEffect(() => {
    const fetchCustomer = async () => {
      const customerData = await CustomerService.getCustomer(id);
      setState((prevState) => ({ ...prevState, customer: customerData }));
    };

    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEmpty(services)) actions.loadServices();
    if (isEmpty(users)) actions.loadUsers();
  });

  const onClickAdd = async () => {
    if (isEmpty(projects)) {
      await actions.loadProjects({ customer: id });
    }
    setState((prevState) => ({ ...prevState, adding: true }));
    actions.showModalSuccess("callDetailsModal");
  };

  const handleChangeFilters = (field, input) => {
    setState((prevState) => ({
      ...prevState,
      filters: { ...prevState.filters, [field]: input }
    }));
  };

  const handleChangeDate = (startDate, endDate) => {
    if (startDate) {
      setState((prevState) => ({
        ...prevState,
        filters: {
          ...prevState.filters,
          arrival_date: moment(startDate).startOf("day")
        }
      }));
    }
    if (endDate) {
      setState((prevState) => ({
        ...prevState,
        filters: {
          ...prevState.filters,
          completed_date: moment(endDate).endOf("day")
        }
      }));
    }
  };

  const resetFilters = () => {
    setState((prevState) => ({
      ...prevState,
      filters: {
        assigned_to: [],
        stage: [],
        arrival_date: null,
        completed_date: null
      }
    }));
  };

  const fetchCalls = async (state) => {
    state.filtered = {
      ...state.filtered,
      customer: [{ value: id }]
    };
    const { calls, pages } = await CallService.loadCalls(state);
    setState((prevState) => ({
      ...prevState,
      callsData: calls,
      pagesData: pages
    }));
  };

  const onClickView = async (callId) => {
    const data = s.callsData.find((c) => c._id === callId);
    await actions.getProject(data?.project);
    setState((prevState) => ({ ...prevState, call: data, adding: false }));
    await actions.getCall(callId, true);
  };

  const onClickDelete = (callId, isRecurring = false) => {
    actions.requestCallId(callId, false);
    setState((prevState) => ({ ...prevState, deleteRecurring: isRecurring }));
  };

  const handleAdd = async (c) => {
    const newCall = await actions.createCall(c);
    await actions.hideModal();
    const formattedCall = {
      ...newCall,
      call_type: newCall.call_type._id,
      customer: newCall.customer._id,
      project: newCall.project._id,
      service: newCall.service._id,
      stage: newCall.stage._id,
      workplace: newCall.workplace._id
    };
    setState((prevState) => ({
      ...prevState,
      callsData: [formattedCall, ...s.callsData]
    }));
  };

  const handleSave = async (c) => {
    setLoading(true);
    c._id = call._id;
    const stage = codes.find((code) => code._id === c.stage);
    if (stage.label === "Completed") {
      c.completed_by = auth.user._id;
    }
    await actions.updateCall(c);
    await actions.hideModal();
    setLoading(false);
  };

  const handleDelete = async () => {
    await actions.deleteCall(callToDelete);
    const newCalls = [...s.callsData].filter(
      (item) => item._id !== callToDelete
    );
    setState((prevState) => ({
      ...prevState,
      callsData: newCalls
    }));
  };

  const callForm = (
    <CallForm
      call={s.adding ? {} : call}
      contracts={contracts}
      users={users}
      auth={auth}
      customer={s.adding ? s.customer : project.customer}
      customers={s.adding ? [s.customer] : [project.customer]}
      services={services}
      projects={s.adding ? projects : [project]}
      project={s.adding ? null : project}
      codes={codes}
      onAdd={handleAdd}
      onSave={handleSave}
      onCancel={actions.hideModal}
      saving={savingCall || loading}
      editing
      adding={s.adding}
      recurring={s.recurring}
    />
  );

  return (
    <React.Fragment>
      <HeaderWrapper
        breadcrumbs={[{ label: s.customer?.name }, { label: "Calls" }]}
        isMobile={isMobile}
        onClickAdd={onClickAdd}
        onClickAction={[
          {
            action: () =>
              setState((prevState) => ({ ...prevState, open: !s.open })),
            icon: <IoFunnel />
          }
        ]}
      />
      <Row>
        <Col md={12}>
          <FiltersComponent
            open={s.open}
            filters={s.filters}
            focusedInput={s.focusedInput}
            onChangeFilters={handleChangeFilters}
            onChangeDate={handleChangeDate}
            onFocusChange={(fi) =>
              setState((prevState) => ({ ...prevState, focusedInput: fi }))
            }
            onReset={resetFilters}
            fields={["Status", "Date", "Assigned To"]}
          />
        </Col>
      </Row>
      <div className="content-wrapper">
        <CallList
          serverSide
          data={s.callsData}
          users={users}
          projects={[]}
          pages={s.pagesData}
          fetchData={fetchCalls}
          isMobile={isMobile}
          filtered={s.filters}
          onClickView={onClickView}
          onClickDelete={onClickDelete}
        />
        <Modal
          id="callDetailsModal"
          title={s.adding ? "Create Call" : "Edit Call"}
          body={callForm}
          close={actions.hideModal}
        />
        <ConfirmModal
          id="callConfirmModal"
          title="Delete Call"
          body="Are you sure you want to delete this call?"
          close={actions.hideModal}
          confirm={handleDelete}
        />
      </div>
    </React.Fragment>
  );
};

CustomerCallsPage.propTypes = {
  actions: PropTypes.object.isRequired,
  project: PropTypes.object,
  contracts: PropTypes.array,
  users: PropTypes.array,
  auth: PropTypes.object,
  codes: PropTypes.array,
  services: PropTypes.array,
  call: PropTypes.object,
  isMobile: PropTypes.bool,
  savingCall: PropTypes.func,
  callToDelete: PropTypes.object,
  projects: PropTypes.array
};

const mapStatesToProps = (state) => ({
  state: state.reducers,
  project: state.reducers.project,
  projects: state.reducers.projects,
  call: state.reducers.call,
  codes: state.reducers.codes,
  users: state.reducers.users,
  services: state.reducers.services,
  customers: state.reducers.customers,
  saving: state.reducers.savingProject,
  loading: state.reducers.loading,
  callToDelete: state.reducers.callToDelete
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...customerActions,
      ...projectActions,
      ...callActions,
      ...codeActions,
      ...serviceActions,
      ...userActions,
      ...modalActions
    },
    dispatch
  )
});

export default connect(mapStatesToProps, mapDispatchToProps)(CustomerCallsPage);
